import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { IconButton } from "@package/subframe/components/IconButton";
import { Loader } from "@package/subframe/components/Loader";
import {
  FeatherArrowLeft,
  FeatherCheck,
  FeatherPlus,
  FeatherX,
} from "@subframe/core";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { format } from "date-fns";

import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useGetFireExtinguisherCheckQueryControllerGet } from "@/generated/fire-extinguisher-checks/fire-extinguisher-checks";
import type { FireExtinguisherCheckItemResponseDto } from "@/generated/model";
import { FIRE_EXTINGUISHER_CHECKS } from "@/lib/fire-extinguisher-checks";
import { memberDisplayName } from "@/lib/member-display";
import { canRaiseFollowUp } from "@/lib/roles";

export const Route = createFileRoute(
  "/_authenticated/fire-extinguisher-checks/$checkId",
)({
  component: FireExtinguisherCheckDetailPage,
});

function FireExtinguisherCheckDetailPage() {
  const { checkId } = Route.useParams();
  const navigate = useNavigate();
  const { isPlatformAdmin, userRoleType } = useAuth();
  const { selectedNurseryId, nurseryRole } = useNursery();

  const canRaise = canRaiseFollowUp({
    isPlatformAdmin,
    userRoleType,
    nurseryRole,
  });

  // Always refetch on open: a follow-up on a failed unit is usually completed
  // elsewhere (Task Manager, another device), and the app's global cache
  // (5-minute staleTime, refetchOnMount false) would otherwise show its old
  // status until a reload.
  const { data, isLoading } = useGetFireExtinguisherCheckQueryControllerGet(
    checkId,
    {
      query: { refetchOnMount: "always" },
    },
  );
  const check = data?.data;

  const goBack = () => navigate({ to: "/fire-extinguisher-checks" });

  const raiseFollowUp = (item: FireExtinguisherCheckItemResponseDto) => {
    navigate({
      to: "/follow-up-actions/create",
      search: {
        nurseryId: check?.nurseryId ?? selectedNurseryId ?? "",
        scheduledQuestionnaireId: "",
        answerId: "",
        questionText: "",
        checklistName: "",
        fireExtinguisherCheckId: checkId,
        fireExtinguisherCheckItemId: item.id,
        extinguisherName: item.extinguisherName,
      },
    });
  };

  return (
    <div className="flex w-full max-w-2xl flex-col items-start gap-6 bg-white animate-fade-in">
      <div className="flex w-full items-start gap-2">
        <IconButton
          aria-label="Back"
          icon={<FeatherArrowLeft />}
          onClick={goBack}
        />
        <div className="flex grow shrink-0 basis-0 flex-col items-start gap-2">
          <span className="w-full whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
            Fire Extinguisher Visual Checks
          </span>
          {check && (
            <span className="text-body font-body text-subtext-color">
              {format(new Date(check.submittedAt), "d MMM yyyy, HH:mm")} ·
              Logged by{" "}
              {check.recordedBy
                ? memberDisplayName(check.recordedBy)
                : "Unknown"}
            </span>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Loader size="large" />
        </div>
      ) : !check ? (
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-neutral-200 p-6">
          <span className="text-body font-body text-default-font">
            This check could not be found.
          </span>
        </div>
      ) : (
        <div className="flex w-full flex-col items-start gap-3">
          {check.items.map((item) => (
            <div
              key={item.id}
              className="flex w-full flex-col items-start gap-3 rounded-lg border border-neutral-200 p-4"
            >
              <div className="flex w-full flex-wrap items-center gap-3">
                <div className="flex grow flex-col items-start gap-0.5">
                  <span className="text-body-bold font-body-bold text-default-font">
                    {item.extinguisherName}
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    {item.roomName}
                  </span>
                </div>
                {item.passed ? (
                  <Badge variant="success">Passed</Badge>
                ) : (
                  <Badge variant="error">Failed</Badge>
                )}
              </div>

              <div className="grid w-full grid-cols-1 gap-1.5 sm:grid-cols-2">
                {FIRE_EXTINGUISHER_CHECKS.map((c) => {
                  const ok = item[c.key];
                  return (
                    <div
                      key={c.key}
                      className="flex items-center justify-between gap-2"
                    >
                      <span className="text-body font-body text-default-font">
                        {c.label}
                      </span>
                      {ok ? (
                        <FeatherCheck className="h-4 w-4 text-success-600" />
                      ) : (
                        <FeatherX className="h-4 w-4 text-error-600" />
                      )}
                    </div>
                  );
                })}
              </div>

              {item.comment && (
                <span className="text-body font-body text-subtext-color">
                  “{item.comment}”
                </span>
              )}

              {item.followUpActions.length > 0 && (
                <div className="flex w-full flex-col items-start gap-1 border-t border-solid border-neutral-100 pt-2">
                  <span className="text-caption-bold font-caption-bold text-subtext-color">
                    Follow-up tasks
                  </span>
                  {item.followUpActions.map((action) => (
                    <div
                      key={action.id}
                      className="flex w-full items-center gap-2"
                    >
                      <span className="grow text-body font-body text-default-font">
                        {action.description}
                      </span>
                      <Badge
                        variant={
                          action.status === "COMPLETED"
                            ? "success"
                            : action.status === "OVERDUE"
                              ? "error"
                              : "neutral"
                        }
                      >
                        {action.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}

              {!item.passed && canRaise && (
                <Button
                  variant="neutral-secondary"
                  size="small"
                  icon={<FeatherPlus />}
                  onClick={() => raiseFollowUp(item)}
                >
                  Raise follow-up
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
